import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, ArrowRight, Star, Shield, Zap, Bot, ChevronRight } from 'lucide-react';
import { api } from '../lib/api';
import { ListingCard, ListingCardSkeleton } from '../components/ListingCard';
import { CategoryCard } from '../components/CategoryCard';
import { Button } from '../components/ui/button';

export default function HomePage() {
  const [categories, setCategories] = useState([]);
  const [featuredListings, setFeaturedListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [cats, listings] = await Promise.all([
        api.getCategories(),
        api.getListings({ limit: 8, featured: false })
      ]);
      setCategories(cats.filter(c => !c.parent_id));
      setFeaturedListings(listings.listings || []);
    } catch (e) {
      console.error('Error loading data:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/explorar?search=${encodeURIComponent(searchQuery)}`);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0C]" data-testid="home-page">
      {/* Hero Section */}
      <section 
        className="relative min-h-[90vh] flex items-center justify-center overflow-hidden"
        style={{
          backgroundImage: `url(https://images.pexels.com/photos/35854403/pexels-photo-35854403.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1024)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0C]/70 via-[#0A0A0C]/60 to-[#0A0A0C]" />
        
        {/* Ambient glows */}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-600/20 rounded-full blur-[120px]" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-600/20 rounded-full blur-[120px]" />

        <div className="relative z-10 max-w-5xl mx-auto px-6 text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 backdrop-blur-md mb-8 animate-fade-in">
            <img src="/logo64.png" alt="" className="w-5 h-5" />
            <span className="text-sm text-zinc-300">La Genética se encuentra aquí</span>
          </div>

          {/* Heading */}
          <h1 className="text-4xl sm:text-5xl lg:text-7xl font-light tracking-tighter text-zinc-100 mb-6 animate-fade-in stagger-1 font-[Outfit]">
            Encuentra tu próximo
            <span className="block text-gradient font-medium">compañero exótico</span>
          </h1>

          <p className="text-lg text-zinc-400 max-w-2xl mx-auto mb-10 animate-fade-in stagger-2">
            Conectamos criadores premium con compradores apasionados. 
            Reptiles, anfibios, invertebrados y más.
          </p>

          {/* Search Bar */}
          <form onSubmit={handleSearch} className="max-w-3xl mx-auto animate-fade-in stagger-3">
            <div className="w-full backdrop-blur-2xl bg-white/5 border border-white/10 rounded-full p-2 flex items-center shadow-2xl hover:border-white/20 transition-colors">
              <div className="flex-1 flex items-center gap-3 px-4">
                <Search className="w-5 h-5 text-zinc-500" />
                <input
                  type="text"
                  placeholder="Buscar por especie, morph, criador..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="flex-1 bg-transparent border-none outline-none text-zinc-100 placeholder-zinc-500"
                  data-testid="hero-search-input"
                />
              </div>
              <Button 
                type="submit"
                className="bg-zinc-100 text-zinc-900 hover:bg-white rounded-full px-6"
                data-testid="hero-search-btn"
              >
                Buscar
              </Button>
            </div>
          </form>

          {/* Quick links */}
          <div className="flex flex-wrap items-center justify-center gap-3 mt-8 animate-fade-in stagger-4">
            <span className="text-sm text-zinc-500">Popular:</span>
            <Link to="/categoria/serpientes" className="text-sm text-zinc-400 hover:text-zinc-100 transition-colors">Ball Python</Link>
            <span className="text-zinc-700">•</span>
            <Link to="/categoria/geckos" className="text-sm text-zinc-400 hover:text-zinc-100 transition-colors">Leopard Gecko</Link>
            <span className="text-zinc-700">•</span>
            <Link to="/categoria/anfibios" className="text-sm text-zinc-400 hover:text-zinc-100 transition-colors">Axolotl</Link>
            <span className="text-zinc-700">•</span>
            <Link to="/categoria/invertebrados" className="text-sm text-zinc-400 hover:text-zinc-100 transition-colors">Tarántulas</Link>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 rounded-full border-2 border-white/20 flex items-start justify-center p-2">
            <div className="w-1 h-2 bg-white/40 rounded-full" />
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-20 md:py-28" data-testid="categories-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-end justify-between mb-10">
            <div>
              <span className="text-xs font-semibold tracking-[0.2em] uppercase text-zinc-500 mb-2 block">Explora</span>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-light tracking-tight text-zinc-100 font-[Outfit]">
                Categorías
              </h2>
            </div>
            <Link to="/categorias" className="flex items-center gap-1 text-sm text-zinc-400 hover:text-zinc-100 transition-colors">
              Ver todas <ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="aspect-[4/3] skeleton rounded-2xl" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              {categories.map((category, index) => (
                <CategoryCard 
                  key={category.id} 
                  category={category}
                  size={index === 0 ? 'large' : 'normal'}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Featured Listings */}
      <section className="py-20 md:py-28 bg-[#0A0A0C]" data-testid="featured-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-end justify-between mb-10">
            <div>
              <span className="text-xs font-semibold tracking-[0.2em] uppercase text-zinc-500 mb-2 block">Recién llegados</span>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-light tracking-tight text-zinc-100 font-[Outfit]">
                Anuncios destacados
              </h2>
            </div>
            <Link to="/explorar" className="flex items-center gap-1 text-sm text-zinc-400 hover:text-zinc-100 transition-colors">
              Ver todos <ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[...Array(4)].map((_, i) => <ListingCardSkeleton key={i} />)}
            </div>
          ) : featuredListings.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {featuredListings.map(listing => (
                <ListingCard key={listing.id} listing={listing} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16 glass-card">
              <p className="text-zinc-500">Aún no hay anuncios. ¡Sé el primero en publicar!</p>
              <Link to="/registro?role=seller">
                <Button className="mt-4 bg-zinc-100 text-zinc-900">Comenzar a vender</Button>
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 md:py-28" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-xs font-semibold tracking-[0.2em] uppercase text-zinc-500 mb-2 block">¿Por qué MorphMX?</span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-light tracking-tight text-zinc-100 font-[Outfit]">
              La plataforma premium
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Feature 1 */}
            <div className="glass-card p-8 hover:border-purple-500/20 transition-colors group">
              <div className="w-12 h-12 rounded-2xl bg-purple-500/10 flex items-center justify-center mb-6 group-hover:glow-purple transition-shadow">
                <Shield className="w-6 h-6 text-purple-400" />
              </div>
              <h3 className="text-xl font-medium text-zinc-100 mb-3 font-[Outfit]">Criadores verificados</h3>
              <p className="text-zinc-500">
                Todos nuestros vendedores pasan por un proceso de verificación. Compra con confianza.
              </p>
            </div>

            {/* Feature 2 */}
            <div className="glass-card p-8 hover:border-blue-500/20 transition-colors group">
              <div className="w-12 h-12 rounded-2xl bg-blue-500/10 flex items-center justify-center mb-6 group-hover:glow-blue transition-shadow">
                <Bot className="w-6 h-6 text-blue-400" />
              </div>
              <h3 className="text-xl font-medium text-zinc-100 mb-3 font-[Outfit]">Recepcionista IA</h3>
              <p className="text-zinc-500">
                Los vendedores premium tienen IA que responde consultas 24/7 con información precisa.
              </p>
            </div>

            {/* Feature 3 */}
            <div className="glass-card p-8 hover:border-emerald-500/20 transition-colors group">
              <div className="w-12 h-12 rounded-2xl bg-emerald-500/10 flex items-center justify-center mb-6 group-hover:glow-emerald transition-shadow">
                <Zap className="w-6 h-6 text-emerald-400" />
              </div>
              <h3 className="text-xl font-medium text-zinc-100 mb-3 font-[Outfit]">Sin comisiones</h3>
              <p className="text-zinc-500">
                No cobramos por venta. Paga una suscripción fija y vende sin límites.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-28" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="relative glass-card p-10 md:p-16 text-center overflow-hidden">
            {/* Background glow */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-gradient-to-b from-purple-600/20 to-transparent blur-[100px]" />
            
            <div className="relative z-10">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-light tracking-tight text-zinc-100 mb-4 font-[Outfit]">
                ¿Eres criador?
              </h2>
              <p className="text-zinc-400 max-w-xl mx-auto mb-8">
                Únete a la comunidad de criadores premium en México. 
                Vende más, automatiza tu atención y crece tu negocio.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link to="/registro?role=seller">
                  <Button className="bg-zinc-100 text-zinc-900 hover:bg-white px-8" data-testid="cta-sell-btn">
                    Comenzar a vender
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <Link to="/precios">
                  <Button variant="outline" className="border-white/10 text-zinc-300 hover:bg-white/5">
                    Ver planes
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
